import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook reutilizável para persistir preferências de tabela (sort, colunas, etc.)
 * no banco de dados por usuário.
 *
 * @param {string} tableKey - Chave única da tabela (ex: "contatos", "empresas", "produtos")
 * @param {object} defaults - Valores padrão: { sortBy, sortDir, columnVisibility }
 * @returns {{ prefs, updatePrefs, loaded }}
 */
export function useTablePreferences(tableKey, defaults = {}) {
  const [prefs, setPrefs] = useState({
    sortBy: defaults.sortBy || "created_at",
    sortDir: defaults.sortDir || "desc",
    columnVisibility: defaults.columnVisibility || {},
    columnOrder: defaults.columnOrder || [],
  });
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);

  // Load from backend on mount
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/table-prefs/${tableKey}`, { withCredentials: true })
      .then(res => {
        if (!cancelled && res.data && Object.keys(res.data).length > 0) {
          setPrefs(prev => ({ ...prev, ...res.data }));
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [tableKey]);

  // Debounced save to backend (500ms after last change)
  const saveToBackend = useCallback((newPrefs) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios.put(`${API}/table-prefs/${tableKey}`, newPrefs, { withCredentials: true }).catch(() => {});
    }, 500);
  }, [tableKey]);

  // Update a specific pref key and auto-save
  const updatePrefs = useCallback((partial) => {
    setPrefs(prev => {
      const next = { ...prev, ...partial };
      saveToBackend(next);
      return next;
    });
  }, [saveToBackend]);

  return { prefs, updatePrefs, loaded };
}
